import { useMemo } from 'react'
import { ComboBoxOption } from 'components/ComboBox/useComboBoxState'
import { OptionsGroup } from 'components/ComboBox/types'

export type GroupedOptionsArgs<T> = {
	data?: T[],
	getName: ( option: T ) => string,
	getGroupBy: ( option: T ) => keyof T,
	getGroupLabel: ( option: T ) => string,
	sortCallback?: ( groups: OptionsGroup<T>[] ) => OptionsGroup<T>[],
};

/** A flat option carrying its group label, for MUI `Autocomplete`'s `groupBy`. */
export type GroupedOption<T> = ComboBoxOption<T> & { groupLabel: string };

/**
 * Grouping + sorting, shared by both presentation branches so group membership
 * and ordering can't drift between EUI and MUI.
 */
export default function useGroupedOptions<T>( {
	data,
	getName,
	getGroupBy,
	getGroupLabel,
	sortCallback,
}: GroupedOptionsArgs<T> ) {
	function buildOption( currentItem: T ) {
		return { label: getName( currentItem ), value: currentItem }
	}

	const groups: OptionsGroup<T>[] = useMemo( () => {
		const built: OptionsGroup<T>[] = []
		data?.forEach( ( item: T ) => {
			if ( typeof item === 'object' && item ) {
				let groupIndex: number = built.findIndex( ( group ) => group.id === item[ getGroupBy( item ) ] as unknown as string )
				if ( groupIndex === -1 ) {
					built.push( { label: getGroupLabel( item ), options: [], id: item[ getGroupBy( item ) ] as unknown as string } )
					groupIndex = built.length - 1
				}
				built[ groupIndex ].options.push( buildOption( item ) )
			}
		} )
		if ( sortCallback ) {
			return sortCallback( built )
		}
		built.forEach( ( group ) => group.options.sort( ( a, b ) => ( ( a.label > b.label ) ? 1 : ( a.label < b.label ) ? -1 : 0 ) ) )
		return built.sort( ( a, b ) => ( ( a.label > b.label ) ? 1 : ( a.label < b.label ) ? -1 : 0 ) )
	}, [ data ] )

	// MUI's `groupBy` requires options already ordered by group, which the
	// group-by-group flatten guarantees.
	const flatOptions: GroupedOption<T>[] = useMemo( () => {
		return groups.flatMap( ( group ) => group.options
			.filter( ( option ): option is { label: string, value: T } => option.value !== undefined )
			.map( ( option ) => ( { label: option.label ?? '', value: option.value, groupLabel: group.label } ) ) )
	}, [ groups ] )

	return { groups, flatOptions, buildOption }
}
